/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Pagination,
    TableRow,
    TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PaginationItem from '@mui/material/PaginationItem';
import TableViewIcon from '@mui/icons-material/TableView';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import { customersAPI } from '../../services/apiService';
import './Customers.css';

function Customers() {
    const { t, i18n } = useTranslation();

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [openFormDialog, setOpenFormDialog] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [formData, setFormData] = useState({
        full_name: '',
        phone_number: '',
        credit: '',
    });

    const [submitting, setSubmitting] = useState(false);
    const [search, setSearch] = useState('');

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const formatNumberByLanguage = (num) => {
        const locale = i18n.language === 'fa' ? 'fa-IR' : 'en-US';
        return new Intl.NumberFormat(locale).format(num);
    };

    const handleChangePage = (event, value) => {
        setCurrentPage(value);
    };

    const fetchCustomers = useCallback(async () => {
        try {
            setLoading(true);
            const result = await customersAPI.getAll();

            let data = [];
            if (Array.isArray(result)) data = result;
            else if (Array.isArray(result?.data)) data = result.data;

            setCustomers(data);
        } catch (err) {
            console.error(err);
            setError(err.message || t('failed_to_fetch_customers'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    const formatPhoneNumber = (phoneNumber) => {
        const phone = String(phoneNumber || '');
        if (i18n.language !== 'fa') {
            return phone;
        }
        const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
        return phone.replace(/\d/g, (digit) => persianDigits[Number(digit)]);
    };
    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);
    const toEnglishDigits = (value = '') =>
        String(value).replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));

    const toPersianDigits = (value = '') => String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

    const handleOpenAddDialog = () => {
        setEditingCustomer(null);
        setFormData({
            full_name: '',
            phone_number: '',
            credit: '',
        });
        setOpenFormDialog(true);
    };

    const handleOpenEditDialog = (customer) => {
        setEditingCustomer(customer);
        setFormData({
            full_name: customer.full_name || '',
            phone_number: customer.phone_number || '',
            credit: toEnglishDigits(customer.credit ?? ''),
        });
        setOpenFormDialog(true);
    };

    const handleCloseFormDialog = () => {
        setOpenFormDialog(false);
        setEditingCustomer(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'credit') {
            const englishOnly = toEnglishDigits(value).replace(/\D/g, '');
            setFormData((prev) => ({
                ...prev,
                credit: englishOnly,
            }));
            return;
        }

        if (name === 'phone_number') {
            const englishOnly = toEnglishDigits(value).replace(/\D/g, '').slice(0, 11);
            setFormData((prev) => ({
                ...prev,
                phone_number: englishOnly,
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.full_name.trim() || !formData.phone_number.trim()) {
            setSnackbar({
                open: true,
                message: t('please_fill_required_fields'),
                severity: 'error',
            });
            return;
        }
        if (toEnglishDigits(formData.phone_number).length !== 11) {
            setSnackbar({
                open: true,
                message: t('phone_number_must_be_11_digits'),
                severity: 'error',
            });
            return;
        }
        try {
            setSubmitting(true);

            const payload = {
                full_name: formData.full_name.trim(),
                phone_number: toEnglishDigits(formData.phone_number).trim(),
                credit: Number(toEnglishDigits(formData.credit || 0)),
            };

            if (editingCustomer) {
                await customersAPI.update(editingCustomer.id, payload);
                setSnackbar({
                    open: true,
                    message: t('customer_updated_successfully'),
                    severity: 'success',
                });
            } else {
                await customersAPI.create(payload);
                setSnackbar({
                    open: true,
                    message: t('customer_created_successfully'),
                    severity: 'success',
                });
            }

            handleCloseFormDialog();
            fetchCustomers();
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: err.message || t('operation_failed'),
                severity: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const confirmDelete = (customer) => {
        setSelectedCustomer(customer);
        setOpenDeleteDialog(true);
    };

    const handleCloseDelete = () => {
        setOpenDeleteDialog(false);
        setSelectedCustomer(null);
    };

    const handleConfirmDelete = async () => {
        try {
            await customersAPI.remove(selectedCustomer.id);
            setCustomers((prev) => prev.filter((item) => item.id !== selectedCustomer.id));

            setSnackbar({
                open: true,
                message: t('customer_deleted_successfully'),
                severity: 'success',
            });
        } catch (err) {
            console.error(err);
        } finally {
            handleCloseDelete();
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const filteredCustomers = customers.filter((customer) =>
        String(customer.full_name || '')
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const currentItems = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);

    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

    const handleExportExcel = () => {
        const isPersian = i18n.language === 'fa';

        const excelData = filteredCustomers.map((customer) => ({
            [t('full_name')]: customer.full_name || '',
            [t('phone_number')]: customer.phone_number || '',
            [t('credit')]: Number(customer.credit || 0),
            [t('description')]: customer.description || '',
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);

        worksheet['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 40 }];

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, isPersian ? 'مشتریان' : 'Customers');

        const date = new Date().toISOString().slice(0, 10);

        XLSX.writeFile(workbook, `${isPersian ? 'مشتریان' : 'customers'}_${date}.xlsx`);
    };

    return (
        <div className="customers_page">
            <Sidebar />

            <div className="customers_main">
                <Navbar />

                <div className="customers_content">
                    <div className="customers_header">
                        <h2>{t('customers_service')}</h2>

                        <Button
                            variant="outlined"
                            startIcon={<TableViewIcon />}
                            onClick={handleExportExcel}
                        >
                            {t('orders_report.export_excel')}
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenAddDialog}
                        >
                            {t('add_customer')}
                        </Button>
                    </div>

                    <TextField
                        placeholder={t('search')}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                        variant="outlined"
                        size="small"
                        className="search_bar"
                    />

                    <TableContainer component={Paper} className="customers_table">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">{t('full_name')}</TableCell>
                                    <TableCell align="center">{t('phone_number')}</TableCell>
                                    <TableCell align="center">{t('credit')}</TableCell>
                                    <TableCell align="center">{t('description')}</TableCell>
                                    <TableCell align="center">{t('actions')}</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {currentItems.map((customer) => (
                                    <TableRow key={customer.id}>
                                        <TableCell align="center">{customer.full_name}</TableCell>
                                        <TableCell align="center">
                                            {formatPhoneNumber(customer.phone_number)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {Number(customer.credit).toLocaleString(
                                                i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                                            )}
                                        </TableCell>
                                        <TableCell />

                                        <TableCell align="center" className="action_buttons">
                                            <Button
                                                variant="outlined"
                                                className="edit_customer_button"
                                                onClick={() => handleOpenEditDialog(customer)}
                                            >
                                                {t('edit')}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => confirmDelete(customer)}
                                            >
                                                {t('delete')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {totalPages > 1 && (
                            <div className="orders_pagination">
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handleChangePage}
                                    color="primary"
                                    variant="outlined"
                                    hidePrevButton
                                    hideNextButton
                                    renderItem={(item) => (
                                        <PaginationItem
                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                            {...item}
                                            page={
                                                item.page
                                                    ? formatNumberByLanguage(item.page)
                                                    : item.page
                                            }
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </TableContainer>

                    <Dialog
                        open={openFormDialog}
                        onClose={handleCloseFormDialog}
                        className="customer_form_dialog"
                        fullWidth
                        maxWidth="xs"
                    >
                        <DialogTitle>
                            {editingCustomer ? t('edit_customer') : t('add_customer')}
                        </DialogTitle>
                        <DialogContent>
                            <div className="customer_form">
                                <TextField
                                    className="popup_textfield"
                                    margin="dense"
                                    label={t('full_name')}
                                    name="full_name"
                                    fullWidth
                                    value={formData.full_name}
                                    onChange={handleChange}
                                />
                                <TextField
                                    className="popup_textfield"
                                    margin="dense"
                                    label={t('phone_number')}
                                    name="phone_number"
                                    fullWidth
                                    type="text"
                                    inputMode="numeric"
                                    value={
                                        i18n.language === 'fa'
                                            ? toPersianDigits(formData.phone_number)
                                            : formData.phone_number
                                    }
                                    onChange={handleChange}
                                />
                                <TextField
                                    className="popup_textfield"
                                    margin="dense"
                                    label={t('credit')}
                                    name="credit"
                                    fullWidth
                                    type="text"
                                    inputMode="numeric"
                                    value={
                                        i18n.language === 'fa'
                                            ? toPersianDigits(formData.credit)
                                            : formData.credit
                                    }
                                    onChange={handleChange}
                                />
                            </div>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={handleCloseFormDialog}> {t('cancel')} </Button>

                            <Button onClick={handleSubmit} variant="contained">
                                {editingCustomer ? t('update') : t('create')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={openDeleteDialog} onClose={handleCloseDelete}>
                        <DialogTitle>{t('confirm_delete')}</DialogTitle>

                        <DialogContent>
                            <DialogContentText> {t('delete_customer_confirm')} </DialogContentText>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={handleCloseDelete}>{t('no')}</Button>

                            <Button onClick={handleConfirmDelete} color="error">
                                {t('yes_delete')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                    >
                        <Alert severity={snackbar.severity}> {snackbar.message} </Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
}

export default Customers;
